import { useState } from "react";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SectionReveal from "@/components/SectionReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { value: "investor", label: "Investor" },
  { value: "partner", label: "Partner / Importer" },
  { value: "press", label: "Press" },
  { value: "other", label: "Other" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    inquiry_type: "investor",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your full name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Please enter a valid email.");
    if (form.message.trim().length < 10) return toast.error("Please share a short message (10+ characters).");

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Inquiry received. Our team will respond shortly.");
      setForm({ name: "", email: "", organization: "", inquiry_type: "investor", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pt-[72px] bg-bone">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <SectionReveal><div className="overline">Direct Inquiry</div></SectionReveal>
          <SectionReveal delay={120}>
            <h1 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[58px] leading-[1.05] tracking-editorial">
              Speak with our founding team.
            </h1>
          </SectionReveal>
          <SectionReveal delay={240}>
            <p className="mt-8 max-w-md text-ink2 leading-relaxed">
              For investor relations, partnership opportunities, importer programmes, or technical collaboration — please share a brief message and we will respond promptly.
            </p>
          </SectionReveal>

          <SectionReveal delay={360}>
            <div className="mt-12 space-y-6 text-sm">
              <div>
                <div className="overline">Email</div>
                <a href="mailto:brett.rierson@maishapharma.com" data-testid="contact-email-link" className="mt-3 inline-flex items-center gap-2 text-ink hover:text-forest transition-colors">
                  <Mail size={14} className="text-copper" /> brett.rierson@maishapharma.com
                </a>
              </div>
              <div>
                <div className="overline">Location</div>
                <div className="mt-3 inline-flex items-center gap-2 text-ink">
                  <MapPin size={14} className="text-copper" /> Kampala, Uganda
                </div>
              </div>
              <div>
                <div className="overline">Mandate</div>
                <div className="mt-3 text-ink">Export-led · Pharmaceutical-grade · Regulated markets</div>
              </div>
            </div>
          </SectionReveal>
        </div>

        <SectionReveal className="lg:col-span-7" delay={180}>
          <form onSubmit={onSubmit} data-testid="contact-form" className="bg-sand/50 border border-line/60 p-8 md:p-12">
            {submitted ? (
              <div className="py-12 text-center" data-testid="contact-success">
                <div className="overline">Received</div>
                <h2 className="mt-6 font-display text-3xl md:text-4xl tracking-editorial">Thank you.</h2>
                <p className="mt-4 text-ink2 max-w-md mx-auto">Your inquiry has been received. A member of our team will reach out shortly.</p>
                <button
                  type="button"
                  data-testid="contact-send-another"
                  onClick={() => setSubmitted(false)}
                  className="mt-10 inline-flex items-center gap-2 border border-forest text-forest px-6 py-3 text-sm tracking-wide hover:bg-forest hover:text-bone transition-colors"
                >
                  Send another inquiry <ArrowUpRight size={14} />
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="Full name" required>
                    <input
                      type="text"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={onChange("name")}
                      className="w-full bg-transparent border-b border-ink/20 focus:border-forest outline-none py-3 text-base placeholder:text-ink2/60 transition-colors"
                      placeholder="Your full name"
                      required
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={onChange("email")}
                      className="w-full bg-transparent border-b border-ink/20 focus:border-forest outline-none py-3 text-base placeholder:text-ink2/60 transition-colors"
                      placeholder="you@organization.com"
                      required
                    />
                  </Field>
                  <Field label="Organization">
                    <input
                      type="text"
                      data-testid="contact-input-organization"
                      value={form.organization}
                      onChange={onChange("organization")}
                      className="w-full bg-transparent border-b border-ink/20 focus:border-forest outline-none py-3 text-base placeholder:text-ink2/60 transition-colors"
                      placeholder="Company / Fund"
                    />
                  </Field>
                  <Field label="Inquiry type" required>
                    <select
                      data-testid="contact-select-type"
                      value={form.inquiry_type}
                      onChange={onChange("inquiry_type")}
                      className="w-full bg-transparent border-b border-ink/20 focus:border-forest outline-none py-3 text-base text-ink"
                    >
                      {TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="mt-8">
                  <Field label="Message" required>
                    <textarea
                      data-testid="contact-textarea-message"
                      rows={5}
                      value={form.message}
                      onChange={onChange("message")}
                      className="w-full bg-transparent border-b border-ink/20 focus:border-forest outline-none py-3 text-base placeholder:text-ink2/60 transition-colors resize-none"
                      placeholder="A brief note about your interest..."
                      required
                    />
                  </Field>
                </div>
                <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
                  <p className="text-[11px] text-ink2/80 max-w-sm leading-relaxed">
                    We treat all inquiries with discretion. Information is used solely to respond to your request.
                  </p>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 bg-forest text-bone px-7 py-4 text-sm tracking-wide hover:bg-forest/85 transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Sending..." : "Submit inquiry"}
                    <ArrowUpRight size={16} />
                  </button>
                </div>
              </>
            )}
          </form>
        </SectionReveal>
      </div>
    </section>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="overline flex items-center gap-2">
      {label}
      {required && <span className="text-copper">*</span>}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);
